import unescape from 'lodash-es/unescape'

import {
  AnyField,
  FieldType,
  ProductTag,
  ReviewsTranslatedFilterKey,
  ReviewsTranslatedFilterWithValues,
  ReviewsTranslatedFilterWithValuesKey,
} from './types'

/**
 * Yotpo encodes unsafe characters with their corresponding html entity.
 * This function will decode these entities.
 *
 * Yotpo is using both hex notation and the named notation when encodes
 * these characters.
 * - Lodash unescape does not handle &#x27 (')
 *
 * We assume decoding the above mentioned characters won't do any harm when rendered
 * by our react application.
 */
export const decodeHtmlEntities = (value: string): string => {
  return unescape(value).replace(/&#x27;/g, "'")
}

const range = (start: number, end: number) => {
  return Array(end - start + 1)
    .fill(undefined)
    .map((_, index) => start + index)
}

export const rangeOfFilterWeights = (start: number, end: number) =>
  range(start, end).map(number => number.toString())

export const getFilterKey = (value: string | string[]) => {
  if (Array.isArray(value)) {
    return `${value[0]}-${value[value.length - 1]}`
  }
  return value
}

export const getProductTagFromString = (value: string | null): ProductTag | undefined => {
  switch (value) {
    case ProductTag.Tops:
      return ProductTag.Tops
    case ProductTag.Bottoms:
      return ProductTag.Bottoms
    case ProductTag.Others:
      return ProductTag.Others
    default:
      return undefined
  }
}

export function getFieldValue(field: AnyField, value: string) {
  switch (field.type) {
    // Weight is an arbitrary value
    case FieldType.Weight:
      return value
    default: {
      const fieldValue = field.values.find(fieldValue => fieldValue.value === value)
      return fieldValue ? fieldValue.name : undefined
    }
  }
}

export function isReviewMediaImage(src: string): boolean {
  // NOTE: Yotpo seems to store images as `.jpg`, and videos as Kaltura links.
  // It is easier and more reliable to check for a file extension rather than
  // a Kaltura domain.
  const jpgExtensionPattern = new RegExp(`^.+\.(jpg).{0,}$`)
  const matches = jpgExtensionPattern.exec(src)
  return !!matches?.[1]
}

const reviewsTranslatedFilterKeys: {
  [Key in ReviewsTranslatedFilterKey]: Key
} = {
  'rating': 'rating',
  'Photos': 'Photos',
  'Occupation': 'Occupation',
  'Size Purchased': 'Size Purchased',
  'Fit': 'Fit',
  'Height': 'Height',
  'Weight': 'Weight',
  'Length Purchased': 'Length Purchased',
}

export function isReviewsTranslatedFilterKey(
  maybeReviewsFilterKey: string | undefined
): maybeReviewsFilterKey is ReviewsTranslatedFilterKey {
  return (
    typeof maybeReviewsFilterKey === 'string' &&
    Object.keys(reviewsTranslatedFilterKeys).includes(
      maybeReviewsFilterKey as ReviewsTranslatedFilterKey
    )
  )
}

const reviewsTranslatedFilterWithValuesKeys: {
  [Key in ReviewsTranslatedFilterWithValuesKey]: Key
} = {
  'Photos': 'Photos',
  'Occupation': 'Occupation',
  'Size Purchased': 'Size Purchased',
  'Fit': 'Fit',
  'Height': 'Height',
  'Weight': 'Weight',
  'Length Purchased': 'Length Purchased',
}

export function isReviewsTranslatedFilterWithValuesKey(
  maybeReviewsFilterKey: string | undefined
): maybeReviewsFilterKey is ReviewsTranslatedFilterWithValuesKey {
  return (
    typeof maybeReviewsFilterKey === 'string' &&
    Object.keys(reviewsTranslatedFilterWithValuesKeys).includes(
      maybeReviewsFilterKey as ReviewsTranslatedFilterWithValuesKey
    )
  )
}

export function getTranslatedValueFromReviewsFilter(props: {
  filter: ReviewsTranslatedFilterWithValues
  valueKey: string
}): string | undefined {
  if (Object.keys(props.filter.values).includes(props.valueKey)) {
    return (props.filter.values as Record<string, string>)[props.valueKey]
  } else {
    return undefined
  }
}
